import React from 'react';

import { EditIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, useModal, Divider } from '@vlabs/uikit';
import { useWatch, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './GeopositionFilterSubForm.module.sass';

// ponytail: artemy has EventLocationGeoPosition (point), but the form uses bounding box format (origin + delta). Define locally.
export type Longitude = number;
export type Latitude = number;
export type LongitudeDelta = number;
export type LatitudeDelta = number;

export interface GeoPositionBbox {
  origin_longitude: Longitude;
  origin_latitude: Latitude;
  longitude_delta?: LongitudeDelta;
  latitude_delta?: LatitudeDelta;
}

interface CoordinateDisplayProps {
  label: string;
  value: Longitude ;
  delta?: LongitudeDelta ;
}

function CoordinateDisplay({
  label,
  value,
  delta = undefined,
}: CoordinateDisplayProps): React.JSX.Element {
  const parsedDelta = delta ?? null;

  return (
    <div className={st.CoordinateDisplay}>
      <span className={st.CoordinateDisplayLabel}>
        {label}
        :
      </span>
      <span className={st.CoordinateDisplayValue}>
        {Number.isInteger(value) ? value : value.toFixed(4)}
        °
        {!!parsedDelta && (
          <span>
            {' \u00B1 '}
            {Number.isInteger(parsedDelta) ? parsedDelta : parsedDelta.toFixed(2)}
            °
          </span>
        )}
      </span>
    </div>
  );
}

interface GeopositionFilterSubFormProps {
  initialValues: GeoPositionBbox;
  onSubmit: (value: GeoPositionBbox) => void;
}

function GeopositionForm({
  initialValues,
  onSubmit,
}: GeopositionFilterSubFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const {
    handleSubmit: originalHandlerSubmit,
    register, formState: { errors },
  } = useForm({ defaultValues: initialValues });

  const longitude = useWatch({ name: 'origin_longitude' });
  const longitudelDelta = useWatch({ name: 'longitude_delta' });
  const latitude = useWatch({ name: 'origin_latitude' });
  const latitudeDelta = useWatch({ name: 'latitude_delta' });

  const onSetInputValue = (value?: string | null) => {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    const number = parseFloat(value);
    return Number.isNaN(number) ? undefined : number;
  };

  const handleSubmit = (func: (value: GeoPositionBbox) => void) => (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    originalHandlerSubmit(func)(e);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div>
        <h5>{t('entities:chart_form.form.geolocation.title')}</h5>
        <Divider small />
        <div className={st.FormRow}>
          <div className={st.InputContainer}>
            <Control.Input
              label={t('долгота (-180...180)') as unknown as string}
              {...register('origin_longitude', {
                required: validate.required(),
                min: validate.gte(-180),
                max: validate.lte(180),
                validate: {
                  number: validate.number(),
                  validateLatitude: (value?: number): boolean | string => {
                    if (!value && latitude) {
                      return validate.required() as string;
                    }
                    return true;
                  },
                  validateDelta: (value?: number): boolean | string => {
                    if (!value && longitudelDelta) {
                      return validate.required() as string;
                    }
                    return true;
                  },
                },
                setValueAs: onSetInputValue,
              })}
              errors={errors}
              id="origin_longitude"
            />
          </div>
          <Divider small />
          <div className={st.InputContainer}>
            <Control.Input
              id="longitude_delta"
              label={t('погрешность (0...90)') as unknown as string}
              {...register('longitude_delta', {
                min: validate.gte(0),
                max: validate.lte(90),
                validate: validate.number(),
                setValueAs: onSetInputValue,
              })}
              errors={errors}
            />
          </div>
        </div>
        <div className={st.FormRow}>
          <div className={st.InputContainer}>
            <Control.Input
              id="origin_latitude"
              label={t('широта (-90...90)') as unknown as string}
              {...register('origin_latitude', {
                min: validate.gte(-90),
                max: validate.lte(90),
                required: validate.required(),
                validate: {
                  number: validate.number(),
                  validateLongitude: (value?: number): boolean | string => {
                    if (!value && longitude) {
                      return validate.required() as string;
                    }
                    return true;
                  },
                  validateDelta: (value?: number): boolean | string => {
                    if (!value && latitudeDelta) {
                      return validate.required() as string;
                    }
                    return true;
                  },
                },
                setValueAs: onSetInputValue,
              })}
              errors={errors}
            />
          </div>
          <Divider small />
          <div className={st.InputContainer}>
            <Control.Input
              id="latitude_delta"
              label={t('погрешность (0...90)') as unknown as string}
              {...register('latitude_delta', {
                min: validate.gte(0),
                max: validate.lte(90),
                validate: validate.number(),
                setValueAs: onSetInputValue,
              })}
              errors={errors}
            />
          </div>
        </div>
        <Divider small />

        <Control.Button
          fullWidth
          type="submit"
        >
          {t('форма.submit')}
        </Control.Button>
      </div>
    </form>
  );
}

interface GeolocationSubFormProps {
  disabled: boolean;
  initialValues: GeoPositionBbox;
  onSubmit: (value: GeoPositionBbox) => void;
}

export function GeopositionFilterSubForm({
  disabled,
  initialValues,
  onSubmit,
}: GeolocationSubFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal();

  const handleSave = (value: GeoPositionBbox) => {
    onSubmit(value);
    modal.close();
  };

  return (
    <div>
      {modal.wrap(<GeopositionForm
        initialValues={initialValues}
        onSubmit={handleSave}
      />)}
      {!initialValues && (
        <Control.Button
          data-testid="chooseGeolocationButton"
          disabled={disabled}
          fullWidth
          onClick={modal.open}
          variant="outlined"
        >
          {t('задать область')}
        </Control.Button>
      )}
      {!!initialValues && !!initialValues?.origin_longitude && (
        <div className={st.CoordinateRoot}>
          <div data-testid="geolocation.coordinateRoot">
            <CoordinateDisplay
              delta={initialValues.longitude_delta}
              label={t('entities:chart_form.form.geolocation.longitude.label')}
              value={initialValues.origin_longitude}
            />
            <CoordinateDisplay
              delta={initialValues.latitude_delta}
              label={t('entities:chart_form.form.geolocation.latitude.label')}
              value={initialValues.origin_latitude}
            />
          </div>

          <Control.IconButton
            data-testid="editGeolocationButton"
            icon={<EditIcon />}
            kind="secondary"
            onClick={modal.open}
          />
        </div>
      )}
    </div>
  );
}
